import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";
import ChatSearchModal from "../UI/SearchModal";
import { useNavigate } from "react-router-dom";
import Button from "../../components/UI/Button";
import { useIsMobile, useDeviceType } from "../../App";

// Simple Portal component
const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const Sidebar = ({ isOpen, onToggle, activeTab, onNavigate }) => {
  const deviceType = useDeviceType();
  
  const effectiveIsOpen = deviceType === 'mobile' ? isOpen : true;
  
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [expandedInfoBlock, setExpandedInfoBlock] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuBtnRefs = useRef({});
  const menuRef = useRef(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmArchiveId, setConfirmArchiveId] = useState(null);
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [bottomSectionExpanded, setBottomSectionExpanded] = useState(true);
  const navigate = useNavigate();

  const { theme } = useTheme();
  const { t } = useLocalization();
  const { user, logout } = useAuth();
  const {
    chats,
    activeChat,
    setActiveChat,
    prepareNewChat,
    deleteChat,
    updateChatTitle,
    archiveChat,
  } = useChat();
  const isMobile = useIsMobile();

  // const storedActiveChatId =
  //   typeof window !== "undefined" ? localStorage.getItem("activeChatId") : null;

  const handleNewChat = () => {
    prepareNewChat();
  };

  // Save active chat id to localStorage on selection
  const handleSelectChat = (chatId) => {
    navigate(`/chat/${chatId}`);

    if (typeof window !== "undefined") {
      localStorage.setItem("activeChatId", chatId);
    }
  };

  // useEffect(() => {
  //   if (
  //     activeTab === "chat" &&
  //     storedActiveChatId &&
  //     chats.some((c) => c.id === storedActiveChatId) &&
  //     activeChat !== "new"
  //   ) {
  //     setActiveChat(storedActiveChatId);
  //   }
  // }, [activeTab, storedActiveChatId, chats, setActiveChat, activeChat]);

  const handleSelectChatFromModal = (chat) => {
    navigate(`/chat/${chat.id}`);

    if (deviceType === 'mobile') {
      onToggle();
    }
  };

  const openSearchModal = () => {
    setIsSearchModalOpen(true);
  };

  const toggleInfoBlock = (blockId) => {
    setExpandedInfoBlock(expandedInfoBlock === blockId ? null : blockId);
  };

  const handleRename = (chat) => {
    setEditingChatId(chat.id);
    setNewTitle(chat.title);
    setMenuOpenId(null);
  };

  const handleRenameSubmit = async (chatId) => {
    if (newTitle.trim()) {
      setIsRenaming(true);
      await updateChatTitle(chatId, newTitle.trim());
      setIsRenaming(false);
    }
    setEditingChatId(null);
    setNewTitle("");
  };

  const openMenu = (chatId) => {
    const btn = menuBtnRefs.current[chatId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 160;
      
      let left = rect.left + window.scrollX;
      
      if (deviceType === 'tablet' || deviceType === 'mobile') {
        left = rect.right + window.scrollX - menuWidth;
        
        if (left < 20) {
          left = 20;
        }
      } else {
        const sidebarWidth = deviceType === 'tablet' ? 260 : 280;
        if (left + menuWidth > sidebarWidth - 10) {
          left = rect.right + window.scrollX - menuWidth;
        }
      }
      
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: left,
      });
    }
    setMenuOpenId(chatId);
  };

  // Close expanded info blocks when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedInfoBlock(null);
      setMenuOpenId(null);
      setEditingChatId(null);
    }
  }, [isOpen]);

  // Information blocks data
  const infoBlocks = [
    {
      id: "advantages",
      icon: "✨",
      title: t("advantages") || "Переваги",
      items: [
        t("advantage1") || "Швидкі та точні відповіді",
        t("advantage2") || "Підтримка української мови",
        t("advantage3") || "Безкоштовне використання",
        t("advantage4") || "Доступно 24/7",
      ],
    },

    {
      id: "privacy",
      icon: "🔒",
      title: t("privacy") || "Конфіденційність",
      items: [
        t("privacy1") || "Ваші дані захищені",
        t("privacy2") || "Не передаємо інформацію третім особам",
        t("privacy3") || "Можете видалити історію в будь-який час",
        t("privacy4") || "Дотримуємось GDPR",
      ],
    },
  ];

  // Navigation items
  const navigationItems = [
    {
      id: "chat",
      icon: "💬",
      label: t("chat"),
    },
    {
      id: "faq",
      icon: "❓",
      label: t("faq") || "Часті питання",
    },
    // {
    //   id: 'blog',
    //   icon: '📰',
    //   label: t('blog') || 'Блог',
    // },
    {
      id: "profile",
      icon: "👤",
      label: t("profile"),
    },
    {
      id: "settings",
      icon: "⚙️",
      label: t("settings"),
    },
    {
      id: "archive",
      icon: "🗄️",
      label: t("archive"),
    },
  ];

  // Styles
  const sidebarStyles = {
    width: effectiveIsOpen ? (deviceType === 'tablet' ? "260px" : "280px") : "0px",
    minWidth: effectiveIsOpen ? (deviceType === 'tablet' ? "260px" : "280px") : "0px",
    maxWidth: "100vw",
    backgroundColor: theme.isDark ? "#171717" : "#f7f7f8",
    borderRight: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.3s ease",
    overflow: "hidden",
    height: "100dvh",
    maxHeight: "100dvh",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 1000,
    touchAction: "pan-y",
    overscrollBehavior: "contain",
  };

  const sidebarHeaderStyles = {
    padding: "16px",
    borderBottom: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    opacity: effectiveIsOpen ? 1 : 0,
    transition: "opacity 0.3s ease",
    visibility: effectiveIsOpen ? "visible" : "hidden",
  };

  const actionButtonStyles = {
    width: "100%",
    padding: "8px 12px",
    backgroundColor: "transparent",
    border: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    borderRadius: "6px",
    color: theme.text,
    fontSize: "13px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s",
    marginBottom: "6px",
  };

  const scrollableContentStyles = {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    opacity: effectiveIsOpen ? 1 : 0,
    transition: "opacity 0.3s ease",
    visibility: effectiveIsOpen ? "visible" : "hidden",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    paddingBottom: "16px",
  };

  const chatListStyles = {
    padding: "8px 16px",
  };

  const infoCardStyles = {
    backgroundColor: "transparent",
  };

  const infoBlockStyles = {
    padding: "10px",
    marginBottom: "12px",
    backgroundColor: "transparent",
    borderRadius: "8px",
    overflow: "hidden",
  };

  const infoHeaderStyles = {
    padding: "2px 6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "transparent",
    transition: "background-color 0.2s",
  };

  const infoTitleStyles = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "500",
    color: theme.text,
  };

  const infoContentStyles = (isExpanded) => ({
    maxHeight: isExpanded ? "200px" : "0px",
    overflow: "hidden",
    transition: "max-height 0.3s ease",
  });

  const infoListStyles = {
    padding: "12px 16px",
    margin: 0,
    listStyle: "none",
  };

  const infoItemStyles = {
    padding: "6px 0",
    fontSize: "12px",
    color: theme.text,
    lineHeight: "1.4",
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  };

  const sidebarBottomStyles = {
    borderTop: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "12px",
    paddingBottom: "12px",
    backgroundColor: theme.isDark ? "#1a1a1a" : "#f4f5f6",
    opacity: effectiveIsOpen ? 1 : 0,
    transition: "opacity 0.3s ease",
    visibility: effectiveIsOpen ? "visible" : "hidden",
    flexShrink: 0,
  };

  const navItemStyles = (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    margin: "2px 0",
    borderRadius: "6px",
    backgroundColor: active
      ? theme.isDark
        ? "#2a2a2a"
        : "#e5e5e5"
      : "transparent",
    color: theme.text,
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: active ? "500" : "400",
    transition: "background-color 0.2s",
    textDecoration: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
  });

  const userInfoStyles = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    // padding: '3px 0',
    borderRadius: "6px",
    // backgroundColor: theme.isDark ? '#2a2a2a' : '#f0f0f0',
    marginBottom: "8px",
  };

  const userAvatarStyles = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: theme.primary,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: "bold",
  };

  const userNameStyles = {
    fontSize: "15px",
    color: theme.text,
    fontWeight: "600",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  };

  const overlayStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 999,
    display: deviceType === 'mobile' && effectiveIsOpen ? "block" : "none",
  };

  // Theme-based styles
  const menuStyles = {
    position: "absolute",
    top: "100%",
    left: 0,
    minWidth: "120px",
    width: "160px",
    maxWidth: "160px",
    background: theme.isDark ? "#23272f" : "#fff",
    color: theme.isDark ? "#fff" : "#23272f",
    border: `1px solid ${theme.isDark ? "#333" : "#e0e0e0"}`,
    borderRadius: "12px",
    boxShadow: theme.isDark
      ? "0 4px 24px 0 rgba(0,0,0,0.7)"
      : "0 4px 24px 0 rgba(0,0,0,0.08)",
    zIndex: 9999,
    padding: "4px 0",
    transition: "all 0.2s",
  };
  const menuBtnStyles = {
    background: "none",
    border: "none",
    color: theme.isDark ? "#fff" : "#888",
    fontSize: "20px",
    cursor: "pointer",
    marginLeft: "8px",
    padding: "0 4px",
    borderRadius: "6px",
    transition: "background 0.2s",
    alignSelf: "flex-end",
  };
  const menuItemStyles = {
    width: "100%",
    background: "none",
    border: "none",
    color: "inherit",
    textAlign: "left",
    fontSize: "14px",
    padding: "6px 8px",
    cursor: "pointer",
    borderRadius: "0",
    transition: "background 0.2s",
    outline: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const menuItemDanger = {
    ...menuItemStyles,
    color: "#e53935",
    fontWeight: 500,
  };
  const renameInputStyles = {
    width: "90%",
    fontSize: "15px",
    padding: "6px 10px",
    borderRadius: "8px",
    border: `1px solid ${theme.isDark ? "#444" : "#bbb"}`,
    background: theme.isDark ? "#23272f" : "#fff",
    color: theme.isDark ? "#fff" : "#23272f",
    outline: "none",
    margin: "2px 0",
    boxShadow: theme.isDark ? "0 2px 8px 0 #1114" : "0 2px 8px 0 #0001",
    transition: "border 0.2s, background 0.2s",
  };

  useEffect(() => {
    if (!menuOpenId) return;
    const handleClick = (e) => {
      const menuNode = menuRef.current;
      const btnNode = menuBtnRefs.current[menuOpenId];
      if (
        menuNode &&
        !menuNode.contains(e.target) &&
        btnNode &&
        !btnNode.contains(e.target)
      ) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpenId]);

  // Модалка підтвердження видалення
  const ConfirmDeleteModal = ({ open, onCancel, onConfirm }) => {
    if (!open) return null;
    return ReactDOM.createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: theme.isDark ? "#23272f" : "#fff",
            color: theme.text,
            borderRadius: 12,
            padding: "32px 24px",
            minWidth: 280,
            boxShadow: "0 8px 32px 0 #0005",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 20,
              color: theme.text,
            }}
          >
            {t("confirmDeleteChat")}
          </div>
          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            <Button
              onClick={onCancel}
              size={isMobile ? "small" : "medium"}
              variant="secondary"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={onConfirm}
              size={isMobile ? "small" : "medium"}
              variant="danger"
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  // Модалка підтвердження архівації
  const ConfirmArchiveModal = ({ open, onCancel, onConfirm }) => {
    if (!open) return null;
    return ReactDOM.createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: theme.isDark ? "#23272f" : "#fff",
            color: theme.text,
            borderRadius: 12,
            padding: "32px 24px",
            minWidth: 280,
            boxShadow: "0 8px 32px 0 #0005",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 12,
              color: theme.text,
            }}
          >
            {t("confirmArchiveChatTitle") || "Архівувати чат?"}
          </div>
          <div
            style={{
              fontSize: 16,
              marginBottom: "8%",
              color: theme.textSecondary,
            }}
          >
            {t("confirmArchiveChatDesc") ||
              "Після архівації чат зникне зі списку активних чатів, але залишиться в архіві."}
          </div>
          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            <Button
              onClick={onCancel}
              size={isMobile ? "small" : "medium"}
              variant="secondary"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={onConfirm}
              size={isMobile ? "small" : "medium"}
              variant="primary"
            >
              {t("archiveAction")}
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  // Модалка для поширення
  const ShareModal = ({ chat, copied, setCopied, onClose }) => {
    if (!chat) return null;
    const url = `${window.location.origin}/chat/${chat.id}`;

    const handleClose = () => {
      setCopied(false);
      onClose();
    };

    return ReactDOM.createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleClose}
      >
        <div
          style={{
            background: theme.isDark ? "#23272f" : "#fff",
            color: theme.text,
            borderRadius: 12,
            padding: "32px 24px",
            minWidth: isMobile ? 280 : 400,
            boxShadow: "0 8px 32px 0 #0005",
            textAlign: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 20,
              color: theme.text,
            }}
          >
            {t("chatLink")}
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <input
              type="text"
              value={url}
              readOnly
              style={{
                flex: 1,
                fontSize: 15,
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${theme.isDark ? "#444" : "#bbb"}`,
                background: theme.isDark ? "#23272f" : "#fff",
                color: theme.text,
              }}
              onFocus={(e) => e.target.select()}
            />
            <button
              className={`copy-button${copied ? " copy-success" : ""}`}
              onClick={() => {
                navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 30000);
              }}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "none",
                background: theme.primary,
                color: "#fff",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {copied ? (
                "✓ " + t("copied")
              ) : (
                <>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon-xs"
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  >
                    <path d="M12.668 10.667C12.668 9.95614 12.668 9.46258 12.6367 9.0791C12.6137 8.79732 12.5758 8.60761 12.5244 8.46387L12.4688 8.33399C12.3148 8.03193 12.0803 7.77885 11.793 7.60254L11.666 7.53125C11.508 7.45087 11.2963 7.39395 10.9209 7.36328C10.5374 7.33197 10.0439 7.33203 9.33301 7.33203H6.5C5.78896 7.33203 5.29563 7.33195 4.91211 7.36328C4.63016 7.38632 4.44065 7.42413 4.29688 7.47559L4.16699 7.53125C3.86488 7.68518 3.61186 7.9196 3.43555 8.20703L3.36524 8.33399C3.28478 8.49198 3.22795 8.70352 3.19727 9.0791C3.16595 9.46259 3.16504 9.95611 3.16504 10.667V13.5C3.16504 14.211 3.16593 14.7044 3.19727 15.0879C3.22797 15.4636 3.28473 15.675 3.36524 15.833L3.43555 15.959C3.61186 16.2466 3.86474 16.4807 4.16699 16.6348L4.29688 16.6914C4.44063 16.7428 4.63025 16.7797 4.91211 16.8027C5.29563 16.8341 5.78896 16.835 6.5 16.835H9.33301C10.0439 16.835 10.5374 16.8341 10.9209 16.8027C11.2965 16.772 11.508 16.7152 11.666 16.6348L11.793 16.5645C12.0804 16.3881 12.3148 16.1351 12.4688 15.833L12.5244 15.7031C12.5759 15.5594 12.6137 15.3698 12.6367 15.0879C12.6681 14.7044 12.668 14.211 12.668 13.5V10.667ZM13.998 12.665C14.4528 12.6634 14.8011 12.6602 15.0879 12.6367C15.4635 12.606 15.675 12.5492 15.833 12.4688L15.959 12.3975C16.2466 12.2211 16.4808 11.9682 16.6348 11.666L16.6914 11.5361C16.7428 11.3924 16.7797 11.2026 16.8027 10.9209C16.8341 10.5374 16.835 10.0439 16.835 9.33301V6.5C16.835 5.78896 16.8341 5.29563 16.8027 4.91211C16.7797 4.63025 16.7428 4.44063 16.6914 4.29688L16.6348 4.16699C16.4807 3.86474 16.2466 3.61186 15.959 3.43555L15.833 3.36524C15.675 3.28473 15.4636 3.22797 15.0879 3.19727C14.7044 3.16593 14.211 3.16504 13.5 3.16504H10.667C9.9561 3.16504 9.46259 3.16595 9.0791 3.19727C8.79739 3.22028 8.6076 3.2572 8.46387 3.30859L8.33399 3.36524C8.03176 3.51923 7.77886 3.75343 7.60254 4.04102L7.53125 4.16699C7.4508 4.32498 7.39397 4.53655 7.36328 4.91211C7.33985 5.19893 7.33562 5.54719 7.33399 6.00195H9.33301C10.022 6.00195 10.5791 6.00131 11.0293 6.03809C11.4873 6.07551 11.8937 6.15471 12.2705 6.34668L12.4883 6.46875C12.984 6.7728 13.3878 7.20854 13.6533 7.72949L13.7197 7.87207C13.8642 8.20859 13.9292 8.56974 13.9619 8.9707C13.9987 9.42092 13.998 9.97799 13.998 10.667V12.665ZM18.165 9.33301C18.165 10.022 18.1657 10.5791 18.1289 11.0293C18.0961 11.4302 18.0311 11.7914 17.8867 12.1279L17.8203 12.2705C17.5549 12.7914 17.1509 13.2272 16.6553 13.5313L16.4365 13.6533C16.0599 13.8452 15.6541 13.9245 15.1963 13.9619C14.8593 13.9895 14.4624 13.9935 13.9951 13.9951C13.9935 14.4624 13.9895 14.8593 13.9619 15.1963C13.9292 15.597 13.864 15.9576 13.7197 16.2939L13.6533 16.4365C13.3878 16.9576 12.9841 17.3941 12.4883 17.6982L12.2705 17.8203C11.8937 18.0123 11.4873 18.0915 11.0293 18.1289C10.5791 18.1657 10.022 18.165 9.33301 18.165H6.5C5.81091 18.165 5.25395 18.1657 4.80371 18.1289C4.40306 18.0962 4.04235 18.031 3.70606 17.8867L3.56348 17.8203C3.04244 17.5548 2.60585 17.151 2.30176 16.6553L2.17969 16.4365C1.98788 16.0599 1.90851 15.6541 1.87109 15.1963C1.83431 14.746 1.83496 14.1891 1.83496 13.5V10.667C1.83496 9.978 1.83432 9.42091 1.87109 8.9707C1.90851 8.5127 1.98772 8.10625 2.17969 7.72949L2.30176 7.51172C2.60586 7.0159 3.04236 6.6122 3.56348 6.34668L3.70606 6.28027C4.04237 6.136 4.40303 6.07083 4.80371 6.03809C5.14051 6.01057 5.53708 6.00551 6.00391 6.00391C6.00551 5.53708 6.01057 5.14051 6.03809 4.80371C6.0755 4.34588 6.15483 3.94012 6.34668 3.56348L6.46875 3.34473C6.77282 2.84912 7.20856 2.44514 7.72949 2.17969L7.87207 2.11328C8.20855 1.96886 8.56979 1.90385 8.9707 1.87109C9.42091 1.83432 9.978 1.83496 10.667 1.83496H13.5C14.1891 1.83496 14.746 1.83431 15.1963 1.87109C15.6541 1.90851 16.0599 1.98788 16.4365 2.17969L16.6553 2.30176C17.151 2.60585 17.5548 3.04244 17.8203 3.56348L17.8867 3.70606C18.031 4.04235 18.0962 4.40306 18.1289 4.80371C18.1657 5.25395 18.165 5.81091 18.165 6.5V9.33301Z"></path>
                  </svg>
                  {t("copy")}
                </>
              )}
            </button>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              onClick={handleClose}
              size={isMobile ? "small" : "medium"}
              variant="secondary"
            >
              {t("close")}
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  // Logout Confirmation Modal
  const LogoutConfirmationModal = ({ open, onCancel, onConfirm }) => {
    if (!open) return null;
    return ReactDOM.createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "20px" : "0",
        }}
      >
        <div
          style={{
            background: theme.isDark ? "#23272f" : "#fff",
            color: theme.text,
            borderRadius: isMobile ? 16 : 12,
            padding: isMobile ? "24px 20px" : "32px 24px",
            minWidth: isMobile ? "calc(100% - 40px)" : 280,
            maxWidth: isMobile ? "calc(100% - 40px)" : 400,
            boxShadow: "0 8px 32px 0 #0005",
            textAlign: "center",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <div style={{ 
            fontSize: isMobile ? 18 : 18, 
            marginBottom: isMobile ? 16 : 20, 
            color: theme.text,
            fontWeight: "600"
          }}>
            {t("logoutConfirmation")}
          </div>
          <div
            style={{
              fontSize: isMobile ? 14 : 14,
              marginBottom: isMobile ? 20 : 24,
              color: theme.textSecondary,
              lineHeight: "1.5",
            }}
          >
            {t("logoutConfirmationDesc")}
          </div>
          <div style={{ 
            display: "flex", 
            gap: isMobile ? 12 : 16, 
            justifyContent: "center",
            flexDirection: isMobile ? "column" : "row",
            width: "100%"
          }}>
            <Button
              onClick={onCancel}
              variant="secondary"
              size={isMobile ? "medium" : "large"}
              style={{
                flex: isMobile ? 1 : "none",
                minWidth: isMobile ? "100%" : "140px",
                padding: isMobile ? "14px 24px" : "12px 24px",
                fontSize: isMobile ? "16px" : "17px",
                backgroundColor: theme.isDark ? "#3a3f4b" : "#f5f5f5",
                color: theme.text,
                border: `1px solid ${theme.isDark ? "#4a4f5b" : "#e0e0e0"}`,
                borderRadius: "8px",
                fontWeight: "500",
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={onConfirm}
              variant="danger"
              size={isMobile ? "medium" : "large"}
              style={{
                flex: isMobile ? 1 : "none",
                minWidth: isMobile ? "100%" : "120px",
                padding: isMobile ? "14px 24px" : "12px 24px",
                fontSize: isMobile ? "16px" : "17px",
                backgroundColor: "#dc3545",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                fontWeight: "500",
              }}
            >
              {t("logout")}
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const handleLogoutConfirm = () => {
    logout();
    setShowConfirmLogout(false);
  };

  return (
    <>
      <style>
        {`
          .sidebar-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .sidebar-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .sidebar-scrollbar::-webkit-scrollbar-thumb {
            background: ${theme.isDark ? "#4a4a4a" : "#d1d5db"};
            border-radius: 3px;
          }
          .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${theme.isDark ? "#6a6a6a" : "#9ca3af"};
          }
          
          .action-btn:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f0f0f0"} !important;
            border-color: ${theme.primary} !important;
          }
          
          .chat-item {
            border-radius: 6px;
            transition: all 0.2s ease;
            margin-bottom: 2px;
          }
          
          .chat-item:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f0f0f0"};
          }
          
          .chat-item.active {
            background-color: ${theme.primary} !important;
            color: red !important;
          }
          
          .chat-item.active span {
            color: white !important;
          }
          
          .chat-menu-btn {
            opacity: 0.7;
            transition: all 0.2s ease;
          }
          
          .chat-item:hover .chat-menu-btn {
            opacity: 1;
          }
          
          .chat-menu-btn:hover {
            opacity: 1 !important;
            background-color: ${theme.isDark ? "#2a2a2a" : "#f8f9fa"} !important;
          }
          
          .chat-rename-input {
            background-color: ${theme.background} !important;
            color: ${theme.text} !important;
            border: 1px solid ${theme.primary} !important;
            border-radius: 4px !important;
            padding: 4px 8px !important;
            font-size: 14px !important;
            width: 100% !important;
          }
          
          .info-header:hover {
            background-color: transparent !important;
          }
          
          .nav-item:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f0f0f0"} !important;
          }
          
          .nav-item.active {
            background-color: ${theme.primary} !important;
            color: white !important;
          }
          
          .nav-item.active span {
            color: white !important;
          }
          
          .share-success {
            background-color: ${theme.isDark ? "#1a5d4a" : "#e6f4ea"} !important;
            color: ${theme.isDark ? "#4ade80" : "#137333"} !important;
            border-color: ${theme.isDark ? "#4ade80" : "#137333"} !important;
            animation: copySuccess 0.3s ease;
          }
          @keyframes copySuccess {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          [role="button"][aria-label][tabindex="0"]:focus {
            outline: none !important;
            box-shadow: none !important;
          }
        `}
      </style>

      {/* Overlay for mobile */}
      <div style={overlayStyles} onClick={onToggle} />

      {/* Sidebar */}
      <div className="sidebar" style={sidebarStyles}>
        {/* Header with Action Buttons */}
        <div style={sidebarHeaderStyles}>
          {/* User info */}
          <div
            style={{
              ...userInfoStyles,
              cursor: "pointer",
              transition: "background 0.2s",
              border: null,
              outline: "none",
            }}
            onClick={() => onNavigate && onNavigate("profile")}
            title={t("profile") || "Профіль"}
            tabIndex={0}
            role="button"
            aria-label={t("profile") || "Профіль"}
            onMouseOver={(e) =>
              (e.currentTarget.style.background = theme.isDark
                ? "#23272f"
                : "#e0e0e0")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
            onMouseDown={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <div style={userAvatarStyles}>
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div style={userNameStyles}>{user?.name || "Користувач"}</div>
          </div>
          {/* Action Buttons */}
          <>
            <button
              className="action-btn"
              style={actionButtonStyles}
              onClick={openSearchModal}
              {...(!isMobile && { title: t("searchChats") })}
            >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon"
                  aria-hidden="true"
                >
                  <path d="M8.5 3a5.5 5.5 0 0 1 4.227 9.02l4.127 4.126a.5.5 0 0 1-.638.765l-.07-.057-4.126-4.127A5.5 5.5 0 1 1 8.5 3Zm0 1a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z" />
                </svg>
                {t("searchChats") || "Пошук чатів"}
              </button>

              <button
                className="action-btn"
                style={{ ...actionButtonStyles, marginBottom: 0 }}
                onClick={handleNewChat}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="icon"
                  aria-hidden="true"
                >
                  <path d="M2.6687 11.333V8.66699C2.6687 7.74455 2.66841 7.01205 2.71655 6.42285C2.76533 5.82612 2.86699 5.31731 3.10425 4.85156L3.25854 4.57617C3.64272 3.94975 4.19392 3.43995 4.85229 3.10449L5.02905 3.02149C5.44666 2.84233 5.90133 2.75849 6.42358 2.71582C7.01272 2.66769 7.74445 2.66797 8.66675 2.66797H9.16675C9.53393 2.66797 9.83165 2.96586 9.83179 3.33301C9.83179 3.70028 9.53402 3.99805 9.16675 3.99805H8.66675C7.7226 3.99805 7.05438 3.99834 6.53198 4.04102C6.14611 4.07254 5.87277 4.12568 5.65601 4.20313L5.45581 4.28906C5.01645 4.51293 4.64872 4.85345 4.39233 5.27149L4.28979 5.45508C4.16388 5.7022 4.08381 6.01663 4.04175 6.53125C3.99906 7.05373 3.99878 7.7226 3.99878 8.66699V11.333C3.99878 12.2774 3.99906 12.9463 4.04175 13.4688C4.08381 13.9833 4.16389 14.2978 4.28979 14.5449L4.39233 14.7285C4.64871 15.1465 5.01648 15.4871 5.45581 15.7109L5.65601 15.7969C5.87276 15.8743 6.14614 15.9265 6.53198 15.958C7.05439 16.0007 7.72256 16.002 8.66675 16.002H11.3337C12.2779 16.002 12.9461 16.0007 13.4685 15.958C13.9829 15.916 14.2976 15.8367 14.5447 15.7109L14.7292 15.6074C15.147 15.3511 15.4879 14.9841 15.7117 14.5449L15.7976 14.3447C15.8751 14.128 15.9272 13.8546 15.9587 13.4688C16.0014 12.9463 16.0017 12.2774 16.0017 11.333V10.833C16.0018 10.466 16.2997 10.1681 16.6667 10.168C17.0339 10.168 17.3316 10.4659 17.3318 10.833V11.333C17.3318 12.2555 17.3331 12.9879 17.2849 13.5771C17.2422 14.0993 17.1584 14.5541 16.9792 14.9717L16.8962 15.1484C16.5609 15.8066 16.0507 16.3571 15.4246 16.7412L15.1492 16.8955C14.6833 17.1329 14.1739 17.2354 13.5769 17.2842C12.9878 17.3323 12.256 17.332 11.3337 17.332H8.66675C7.74446 17.332 7.01271 17.3323 6.42358 17.2842C5.90135 17.2415 5.44665 17.1577 5.02905 16.9785L4.85229 16.8955C4.19396 16.5601 3.64271 16.0502 3.25854 15.4238L3.10425 15.1484C2.86697 14.6827 2.76534 14.1739 2.71655 13.5771C2.66841 12.9879 2.6687 12.2555 2.6687 11.333ZM13.4646 3.11328C14.4201 2.334 15.8288 2.38969 16.7195 3.28027L16.8865 3.46485C17.6141 4.35685 17.6143 5.64423 16.8865 6.53613L16.7195 6.7207L11.6726 11.7686C11.1373 12.3039 10.4624 12.6746 9.72827 12.8408L9.41089 12.8994L7.59351 13.1582C7.38637 13.1877 7.17701 13.1187 7.02905 12.9707C6.88112 12.8227 6.81199 12.6134 6.84155 12.4063L7.10132 10.5898L7.15991 10.2715C7.3262 9.53749 7.69692 8.86241 8.23218 8.32715L13.2791 3.28027L13.4646 3.11328ZM15.7791 4.2207C15.3753 3.81702 14.7366 3.79124 14.3035 4.14453L14.2195 4.2207L9.17261 9.26856C8.81541 9.62578 8.56774 10.0756 8.45679 10.5654L8.41772 10.7773L8.28296 11.7158L9.22241 11.582L9.43433 11.543C9.92426 11.432 10.3749 11.1844 10.7322 10.8271L15.7791 5.78027L15.8552 5.69629C16.185 5.29194 16.1852 4.708 15.8552 4.30371L15.7791 4.2207Z"></path>
                </svg>
                {t("newChat") || "Новий чат"}
              </button>
            </>
        </div>

        {/* Scrollable Content */}
        <div 
          style={scrollableContentStyles} 
          className="sidebar-scrollbar"
          onWheel={(e) => {
            // Prevent scroll propagation to body when scrolling sidebar
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // Prevent scroll propagation on mobile
            e.stopPropagation();
          }}
        >
          {/* Chat List - Show on all tabs */}
          <div style={chatListStyles}>
            {chats.filter((chat) => !chat.is_archived).length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: theme.textSecondary,
                      marginBottom: "8px",
                      padding: "0 4px",
                    }}
                  >
                    {t("recentChats") || "Останні чати"}
                  </div>
                  {chats
                    .filter((chat) => !chat.is_archived)
                    .map((chat) => {
                      return (
                        <div
                          key={chat.id}
                          className={`chat-item${activeChat === chat.id ? " active" : ""}`}
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            padding: "4px 8px",
                            justifyContent: "space-between",
                          }}
                          onClick={() => handleSelectChat(chat.id)}
                        >
                          {editingChatId === chat.id ? (
                            <input
                              type="text"
                              value={newTitle}
                              autoFocus
                              onChange={(e) => setNewTitle(e.target.value)}
                              onBlur={() => handleRenameSubmit(chat.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  handleRenameSubmit(chat.id);
                                if (e.key === "Escape") {
                                  setEditingChatId(null);
                                  setNewTitle("");
                                }
                              }}
                              disabled={isRenaming}
                              className="chat-rename-input"
                              style={renameInputStyles}
                            />
                          ) : (
                            <>
                              <span
                                onClick={() => handleSelectChat(chat.id)}
                                style={{
                                  flex: 1,
                                  cursor: "pointer",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: theme.text,
                                  fontWeight: 500,
                                  textShadow: theme.isDark
                                    ? "0 1px 2px #000a"
                                    : "none",
                                }}
                              >
                                {chat.title}
                              </span>
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <button
                                  className="chat-menu-btn"
                                  style={menuBtnStyles}
                                  ref={(el) =>
                                    (menuBtnRefs.current[chat.id] = el)
                                  }
                                  onClick={() =>
                                    openMenu(
                                      menuOpenId === chat.id ? null : chat.id,
                                    )
                                  }
                                >
                                  ⋯
                                </button>
                                {menuOpenId === chat.id && (
                                  <Portal>
                                    <div
                                      className="chat-menu"
                                      style={{
                                        ...menuStyles,
                                        position: "absolute",
                                        top: menuPosition.top,
                                        left: menuPosition.left,
                                      }}
                                      ref={menuRef}
                                    >
                                      <button
                                        style={menuItemStyles}
                                        onClick={() => handleRename(chat)}
                                      >
                                        {/* SVG Edit Icon */}
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          style={{
                                            marginRight: 8,
                                            verticalAlign: "middle",
                                          }}
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M16.862 5.487a2.06 2.06 0 0 1 2.915 2.914l-9.193 9.193a2 2 0 0 1-.707.464l-3.11 1.037a.5.5 0 0 1-.634-.634l1.037-3.11a2 2 0 0 1 .464-.707l9.193-9.193Z"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        {t("rename")}
                                      </button>
                                      <button
                                        style={menuItemStyles}
                                        onClick={async () => {
                                          setConfirmArchiveId(chat.id);
                                          setMenuOpenId(null);
                                        }}
                                      >
                                        {/* SVG Archive Icon */}
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          style={{
                                            marginRight: 8,
                                            verticalAlign: "middle",
                                          }}
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <rect
                                            x="3"
                                            y="7"
                                            width="18"
                                            height="4"
                                            rx="2"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                          />
                                          <path
                                            d="M5 11v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                          />
                                        </svg>
                                        {t("archiveAction")}
                                      </button>
                                      <button
                                        style={menuItemDanger}
                                        onClick={() => {
                                          setConfirmDeleteId(chat.id);
                                          setMenuOpenId(null);
                                        }}
                                      >
                                        {/* SVG Delete Icon */}
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          style={{
                                            marginRight: 8,
                                            verticalAlign: "middle",
                                          }}
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M6 7h12M10 11v4M14 11v4M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12"
                                            stroke="#e53935"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
                                            stroke="#e53935"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        {t("delete")}
                                      </button>
                                    </div>
                                  </Portal>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                </>
              )}
            </div>
        </div>

        {/* Bottom Section with CTA and Info Blocks - Fixed at bottom */}
        <div style={sidebarBottomStyles}>
          {/* Toggle header for bottom section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "1.1rem",
              userSelect: "none",
              color: theme.text,
              gap: 8,
            }}
            onClick={() => setBottomSectionExpanded((v) => !v)}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transition: "transform 0.2s",
                  transform: bottomSectionExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  display: "block",
                  margin: "auto",
                }}
              >
                <path
                  d="M7 10l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 5l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          {bottomSectionExpanded && (
            <>
              {/* Information Blocks */}
              <div style={{ padding: "2px" }}>
                <div style={infoCardStyles}>
                  {infoBlocks.map((block) => (
                    <div key={block.id} style={infoBlockStyles}>
                      <div
                        className="info-header"
                        style={infoHeaderStyles}
                        onClick={() => toggleInfoBlock(block.id)}
                      >
                        <div style={infoTitleStyles}>
                          <span>{block.icon}</span>
                          <span>{block.title}</span>
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            color: theme.textSecondary,
                            transform:
                              expandedInfoBlock === block.id
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        >
                          ▼
                        </span>
                      </div>

                      <div
                        style={infoContentStyles(
                          expandedInfoBlock === block.id,
                        )}
                      >
                        <ul style={infoListStyles}>
                          {block.items.map((item, index) => (
                            <li key={index} style={infoItemStyles}>
                              <span
                                style={{
                                  color: theme.primary,
                                  fontSize: "12px",
                                  fontWeight: "500",
                                }}
                              >
                                •
                              </span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Navigation items */}
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  className="nav-item"
                  style={navItemStyles(item.id === activeTab)}
                  onClick={() => onNavigate(item.id)}
                >
                  <span style={{ fontSize: "14px" }}>{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}

              {/* Logout button */}
              <button
                className="nav-item"
                style={{
                  ...navItemStyles(false),
                  color: "#e53935",
                  borderTop: `1px solid ${theme.border}`,
                  marginTop: "8px",
                  paddingTop: "10px",
                }}
                onClick={() => setShowConfirmLogout(true)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 187.338 187.338"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g>
                    <g>
                      <path d="M125.393,95.594c-1.629-1.183-4.344,0.387-4.153,2.383c0.652,6.86,1.787,13.168,1.565,20.185 c-0.251,7.964-1.063,15.911-1.226,23.877c-14.855,0.447-29.671-0.884-44.524-0.893c0.548-32.136,0.552-64.361,0.547-96.382 c0-2.468-2.462-3.305-4.204-2.567c-0.007-0.009-0.009-0.019-0.016-0.028c-6.534-8.158-20.93-12.415-30.032-17.155 C33.066,19.658,23.02,13.634,14.366,5.905c15.627,1.086,31.122,3.332,46.744,4.475c20.147,1.476,40.181-2.192,60.287,0.449 c0.518,6.438,0.365,12.828-0.014,19.278c-0.349,5.936-1.441,12.691,0.889,18.304c1.24,2.988,5.073,2.447,5.488-0.743 c0.849-6.529,0.191-13.542,0.533-20.172c0.301-5.837,0.205-11.53-1.437-17.124c1.855-1.582,2.002-4.969-1.022-5.523 c-17.917-3.283-36.129-1.422-54.217-1.02C50.938,4.288,30.471-1.66,9.832,0.759C9.667,0.778,9.545,0.849,9.398,0.89 C8.471-0.162,6.583-0.124,5.871,1.638c-9.284,23.017-5.076,49.963-3.882,74.09c1.189,24.004-2.432,48.698,3.473,72.267 c0.334,1.331,1.294,1.836,2.277,1.791c16.22,17.971,41.909,25.269,62.465,36.951c1.38,0.784,2.673,0.392,3.536-0.462 c1.089,0.029,2.159-0.679,2.215-2.184c0.469-12.607,0.787-25.254,1.027-37.913c15.939,2.026,31.894,3.255,47.981,2.666 c1.931-0.071,3.39-1.52,3.453-3.454c0.336-9.966,1.327-19.894,1.514-29.866C130.046,109.53,130.9,99.59,125.393,95.594z M34.15,26.722c11.063,6.375,24.699,15.941,36.992,19.295c0.197,0.054,0.373,0.051,0.553,0.061 c-0.014,1.85-0.069,3.715-0.113,5.575c-7.436-7.919-18.397-13.08-27.733-18.077C31.759,27.104,19.698,21.24,8.643,13.029 c0.195-1.863,0.379-3.724,0.635-5.595C15.703,15.826,24.955,21.424,34.15,26.722z M7.324,51.238 c21.446,9.495,42.479,19.029,61.443,33.057c0.655,0.485,1.36,0.408,1.942,0.066c-0.11,3.016-0.222,6.032-0.333,9.051 C49.454,81.06,27.935,69.847,7.444,56.701C7.397,54.876,7.358,53.057,7.324,51.238z M7.514,59.39 c19.922,13.94,40.71,27.935,62.701,38.383c-0.044,1.224-0.091,2.446-0.135,3.671C50.492,87.807,29.879,75.583,8.315,65.333 c-0.225-0.107-0.439-0.098-0.629-0.038C7.626,63.321,7.569,61.354,7.514,59.39z M8,75.724c-0.082-2.952-0.173-5.887-0.264-8.812 c21.234,11.539,41.176,24.927,61.032,38.668c0.404,0.279,0.792,0.345,1.161,0.326c-0.067,1.998-0.145,3.994-0.205,5.992 C49.528,99.635,28.578,88.526,8.016,76.89C8.006,76.5,8.009,76.113,8,75.724z M8.046,78.971 c20.365,12.666,40.318,26.282,61.537,37.479c-0.025,0.942-0.065,1.885-0.087,2.827c-20.348-11.091-41.51-20.35-61.335-32.464 C8.147,84.203,8.096,81.586,8.046,78.971z M8.176,94.673c0.003-1.933-0.006-3.868-0.012-5.802 C27,102.615,48.156,114.129,69.379,123.634c-0.016,0.787-0.05,1.576-0.063,2.363C50.406,113.002,27.132,107.816,8.176,94.673z M69.266,130.413c-0.02,1.792-0.055,3.586-0.061,5.377c-18.427-13.134-40.962-21.228-60.778-31.947 c-0.09-0.049-0.176-0.06-0.263-0.082c0.002-2.277,0.004-4.56,0.009-6.838C26.42,111.314,49.709,117.929,69.266,130.413z M8.161,106.113c19.908,11.609,40.451,23.03,61.07,33.3c0.007,3.096,0.01,6.191,0.069,9.281 c-17.275-15.029-41.408-20.417-61.05-31.812C8.185,113.294,8.166,109.704,8.161,106.113z M8.532,126.822 c-0.129-2.669-0.165-5.338-0.226-8.008c19.454,13.142,42.913,19.459,61.147,34.445c0.088,3.045,0.151,6.094,0.303,9.13 c-7.177-6.963-19.645-11.459-27.82-15.807c-6.886-3.662-13.787-7.301-20.613-11.072c-2.491-1.377-12.221-5.467-12.788-8.685 C8.534,126.824,8.532,126.824,8.532,126.822z M8.778,131.046c4.503,5.154,16.964,10.73,19.459,12.164 c13.767,7.913,27.702,16.472,41.762,23.762c0.146,2.371,0.307,4.738,0.499,7.103c-13.046-7.478-26.073-15.375-38.38-23.961 c-6.087-4.246-14.773-13.3-22.979-13.956C8.99,134.452,8.888,132.749,8.778,131.046z M10.315,147.036 c-0.001-0.132,0.036-0.248,0.016-0.387c-0.368-2.51-0.649-5.021-0.908-7.529c19.462,14.265,40.189,27.678,61.438,39.104 c0.06,0.642,0.104,1.287,0.169,1.929C50.956,168.578,29.429,160.404,10.315,147.036z M70.843,80.656 C52.738,65.248,29.286,57.289,7.292,49.086c-0.057-3.758-0.066-7.508-0.028-11.257C24.981,54,49.067,63.198,71.114,72.063 C71.023,74.929,70.945,77.787,70.843,80.656z M71.26,67.478C49.593,57.517,26.313,50.845,7.708,35.277 c-0.122-0.102-0.251-0.166-0.381-0.211c0.055-3.044,0.169-6.091,0.316-9.14c11.434,8.338,23.643,15.322,36.084,22.069 c3.103,1.683,22.356,13.161,27.657,13.339C71.333,63.386,71.32,65.424,71.26,67.478z M58.576,51.28 C41.028,44.145,23.378,34.262,7.773,23.465c0.154-2.663,0.347-5.331,0.593-8.004c9.424,7.264,19.688,13.28,29.918,19.337 c11.443,6.774,21.946,14.825,33.198,21.744c-0.001,0.025-0.001,0.05-0.002,0.075C67.538,54.14,62.145,52.73,58.576,51.28z"></path>
                    </g>
                  </g>
                </svg>
                <span>{t("logout") || "Вийти"}</span>
              </button>
            </>
          )}
          {/* Logout button always visible */}
          {!bottomSectionExpanded && (
            <button
              className="nav-item"
              style={{
                ...navItemStyles(false),
                color: "#e53935",
                borderTop: `1px solid ${theme.border}`,
                marginTop: "8px",
                paddingTop: "10px",
                paddingLeft: "33%",
              }}
              onClick={() => setShowConfirmLogout(true)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 187.338 187.338"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <g>
                    <path d="M125.393,95.594c-1.629-1.183-4.344,0.387-4.153,2.383c0.652,6.86,1.787,13.168,1.565,20.185 c-0.251,7.964-1.063,15.911-1.226,23.877c-14.855,0.447-29.671-0.884-44.524-0.893c0.548-32.136,0.552-64.361,0.547-96.382 c0-2.468-2.462-3.305-4.204-2.567c-0.007-0.009-0.009-0.019-0.016-0.028c-6.534-8.158-20.93-12.415-30.032-17.155 C33.066,19.658,23.02,13.634,14.366,5.905c15.627,1.086,31.122,3.332,46.744,4.475c20.147,1.476,40.181-2.192,60.287,0.449 c0.518,6.438,0.365,12.828-0.014,19.278c-0.349,5.936-1.441,12.691,0.889,18.304c1.24,2.988,5.073,2.447,5.488-0.743 c0.849-6.529,0.191-13.542,0.533-20.172c0.301-5.837,0.205-11.53-1.437-17.124c1.855-1.582,2.002-4.969-1.022-5.523 c-17.917-3.283-36.129-1.422-54.217-1.02C50.938,4.288,30.471-1.66,9.832,0.759C9.667,0.778,9.545,0.849,9.398,0.89 C8.471-0.162,6.583-0.124,5.871,1.638c-9.284,23.017-5.076,49.963-3.882,74.09c1.189,24.004-2.432,48.698,3.473,72.267 c0.334,1.331,1.294,1.836,2.277,1.791c16.22,17.971,41.909,25.269,62.465,36.951c1.38,0.784,2.673,0.392,3.536-0.462 c1.089,0.029,2.159-0.679,2.215-2.184c0.469-12.607,0.787-25.254,1.027-37.913c15.939,2.026,31.894,3.255,47.981,2.666 c1.931-0.071,3.39-1.52,3.453-3.454c0.336-9.966,1.327-19.894,1.514-29.866C130.046,109.53,130.9,99.59,125.393,95.594z M34.15,26.722c11.063,6.375,24.699,15.941,36.992,19.295c0.197,0.054,0.373,0.051,0.553,0.061 c-0.014,1.85-0.069,3.715-0.113,5.575c-7.436-7.919-18.397-13.08-27.733-18.077C31.759,27.104,19.698,21.24,8.643,13.029 c0.195-1.863,0.379-3.724,0.635-5.595C15.703,15.826,24.955,21.424,34.15,26.722z M7.324,51.238 c21.446,9.495,42.479,19.029,61.443,33.057c0.655,0.485,1.36,0.408,1.942,0.066c-0.11,3.016-0.222,6.032-0.333,9.051 C49.454,81.06,27.935,69.847,7.444,56.701C7.397,54.876,7.358,53.057,7.324,51.238z M7.514,59.39 c19.922,13.94,40.71,27.935,62.701,38.383c-0.044,1.224-0.091,2.446-0.135,3.671C50.492,87.807,29.879,75.583,8.315,65.333 c-0.225-0.107-0.439-0.098-0.629-0.038C7.626,63.321,7.569,61.354,7.514,59.39z M8,75.724c-0.082-2.952-0.173-5.887-0.264-8.812 c21.234,11.539,41.176,24.927,61.032,38.668c0.404,0.279,0.792,0.345,1.161,0.326c-0.067,1.998-0.145,3.994-0.205,5.992 C49.528,99.635,28.578,88.526,8.016,76.89C8.006,76.5,8.009,76.113,8,75.724z M8.046,78.971 c20.365,12.666,40.318,26.282,61.537,37.479c-0.025,0.942-0.065,1.885-0.087,2.827c-20.348-11.091-41.51-20.35-61.335-32.464 C8.147,84.203,8.096,81.586,8.046,78.971z M8.176,94.673c0.003-1.933-0.006-3.868-0.012-5.802 C27,102.615,48.156,114.129,69.379,123.634c-0.016,0.787-0.05,1.576-0.063,2.363C50.406,113.002,27.132,107.816,8.176,94.673z M69.266,130.413c-0.02,1.792-0.055,3.586-0.061,5.377c-18.427-13.134-40.962-21.228-60.778-31.947 c-0.09-0.049-0.176-0.06-0.263-0.082c0.002-2.277,0.004-4.56,0.009-6.838C26.42,111.314,49.709,117.929,69.266,130.413z M8.161,106.113c19.908,11.609,40.451,23.03,61.07,33.3c0.007,3.096,0.01,6.191,0.069,9.281 c-17.275-15.029-41.408-20.417-61.05-31.812C8.185,113.294,8.166,109.704,8.161,106.113z M8.532,126.822 c-0.129-2.669-0.165-5.338-0.226-8.008c19.454,13.142,42.913,19.459,61.147,34.445c0.088,3.045,0.151,6.094,0.303,9.13 c-7.177-6.963-19.645-11.459-27.82-15.807c-6.886-3.662-13.787-7.301-20.613-11.072c-2.491-1.377-12.221-5.467-12.788-8.685 C8.534,126.824,8.532,126.824,8.532,126.822z M8.778,131.046c4.503,5.154,16.964,10.73,19.459,12.164 c13.767,7.913,27.702,16.472,41.762,23.762c0.146,2.371,0.307,4.738,0.499,7.103c-13.046-7.478-26.073-15.375-38.38-23.961 c-6.087-4.246-14.773-13.3-22.979-13.956C8.99,134.452,8.888,132.749,8.778,131.046z M10.315,147.036 c-0.001-0.132,0.036-0.248,0.016-0.387c-0.368-2.51-0.649-5.021-0.908-7.529c19.462,14.265,40.189,27.678,61.438,39.104 c0.06,0.642,0.104,1.287,0.169,1.929C50.956,168.578,29.429,160.404,10.315,147.036z M70.843,80.656 C52.738,65.248,29.286,57.289,7.292,49.086c-0.057-3.758-0.066-7.508-0.028-11.257C24.981,54,49.067,63.198,71.114,72.063 C71.023,74.929,70.945,77.787,70.843,80.656z M71.26,67.478C49.593,57.517,26.313,50.845,7.708,35.277 c-0.122-0.102-0.251-0.166-0.381-0.211c0.055-3.044,0.169-6.091,0.316-9.14c11.434,8.338,23.643,15.322,36.084,22.069 c3.103,1.683,22.356,13.161,27.657,13.339C71.333,63.386,71.32,65.424,71.26,67.478z M58.576,51.28 C41.028,44.145,23.378,34.262,7.773,23.465c0.154-2.663,0.347-5.331,0.593-8.004c9.424,7.264,19.688,13.28,29.918,19.337 c11.443,6.774,21.946,14.825,33.198,21.744c-0.001,0.025-0.001,0.05-0.002,0.075C67.538,54.14,62.145,52.73,58.576,51.28z"></path>
                  </g>
                </g>
              </svg>
              <span>{t("logout") || "Вийти"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Search Modal */}
      <ChatSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectChat={handleSelectChatFromModal}
        chats={chats}
      />

      <ConfirmDeleteModal
        open={!!confirmDeleteId}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId) deleteChat(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
      />

      <ConfirmArchiveModal
        open={!!confirmArchiveId}
        onCancel={() => setConfirmArchiveId(null)}
        onConfirm={async () => {
          if (confirmArchiveId) await archiveChat(confirmArchiveId);
          setConfirmArchiveId(null);
        }}
      />

      <LogoutConfirmationModal
        open={showConfirmLogout}
        onCancel={() => setShowConfirmLogout(false)}
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
};

export default Sidebar;
